import { StatusCodes } from "http-status-codes"
import { SetStateAction, useCallback, useEffect, useState } from "react"
import { useForm, useWatch } from "react-hook-form"
import { toast } from "react-toastify"
import { digitalManagerApi } from "../../../../../services/api"
import { renderDevicesSelectionLayout } from "../../../../components/Helpers"
import { ApplicationDeviceGet } from "../../../../models/ApplicationDevice"
import { Guid } from "../../../../models/CustomType"
import { DeviceGet } from "../../../../models/Device"

interface ApplicationDevicesProps {
  applicationDevices: ApplicationDeviceGet
  reloadDevices: () => void
}

const handleSave = (applicationId: Guid, checkedDevices: Guid[]) =>
  digitalManagerApi
    .post(`/applications/${applicationId}/devices`, {
      deviceIds: checkedDevices,
    })
    .then((res) => {
      if (res.status === StatusCodes.CREATED) {
        toast.success("Application's devices updated")
      }
    })
    .catch(() => {
      toast.error("Cannot update application's devices")
    })

export const ApplicationDevices = (props: Readonly<ApplicationDevicesProps>) => {
  const { applicationDevices, reloadDevices } = props
  const [devices, setDevices] = useState<DeviceGet[] | null>(null)

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isSubmitting, isDirty },
  } = useForm<{
    deviceIds: string[]
  }>({})

  const deviceIdsValues = useWatch({
    control,
    name: "deviceIds",
  })

  useEffect(() => {
    reset({
      deviceIds: applicationDevices.devices.map((device) => device.id),
    })
  }, [applicationDevices.devices, reset])

  const onSubmit = async (data: { deviceIds: string[] }) => {
    await handleSave(applicationDevices.id, data.deviceIds)
    reloadDevices()
  }

  useEffect(() => {
    digitalManagerApi
      .get<DeviceGet[]>("/devices")
      .then((res) => {
        setDevices(res.data)
      })
      .catch(() => {
        setDevices([])
        toast.error("Error retrieving the list of devices")
      })
  }, [])

  const onClickSelectAll = useCallback(() => {
    if (devices) {
      setValue(
        "deviceIds",
        devices.map((device) => device.id),
        { shouldDirty: true }
      )
    }
  }, [devices, setValue])

  const onClickUnselectAll = useCallback(() => {
    setValue("deviceIds", [], { shouldDirty: true })
  }, [setValue])

  const handleSetCheckedItemsIds = useCallback(
    (ids: SetStateAction<string[]>) => {
      setValue("deviceIds", typeof ids === "function" ? ids(deviceIdsValues) : ids, {
        shouldDirty: true,
      })
    },
    [setValue, deviceIdsValues]
  )

  return renderDevicesSelectionLayout({
    checkedItemsIds: deviceIdsValues,
    setCheckedItemsIds: handleSetCheckedItemsIds,
    devices,
    onClickSelectAll,
    onClickUnselectAll,
    handleSubmit,
    onSubmit,
    isDirty,
    isSubmitting,
  })
}
